import { useState, useEffect } from 'react'
import { useApp } from '../../context/AppContext'
import { sileo } from 'sileo'
import FileDrop from '../File/FileDrop'
import RichTextEditor from '../Shared/RichTextEditor'
import { isHtmlEmpty } from '../../utils/richText'

/**
 * Componente para registrar una nueva solución técnica asociada a un ticket.
 * * El proceso se realiza en tres etapas principales:
 * 1. Creación de la solución en la tabla `solution`.
 * 2. Vinculación mediante la tabla intermedia `ticketSolution`.
 * 3. Carga de archivos adjuntos al Storage y registro en la tabla `file`.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {string|number} props.ticketId - Identificador único del ticket al que se vincula la solución.
 * @param {Function} [props.onAdded] - Callback opcional que se invoca tras completar el registro exitosamente.
 * * @returns {JSX.Element} Formulario de registro de solución con soporte para adjuntos.
 */
const AddSolution = ({ ticketId, onAdded }) => {
  /** @type {Object} Contexto de la aplicación con cliente Supabase y datos del usuario */
  const { supabase, user } = useApp()

  /** @type {[boolean, Function]} Estado de carga para el envío del formulario */
  const [loading, setLoading] = useState(false)

  /** @type {[Array<Object>, Function]} Lista de archivos seleccionados para subir */
  const [files, setFiles] = useState([])
  const [uploadStatus, setUploadStatus] = useState([]) // { name, status: 'pending'|'ok'|'error', message }

  /** @type {[Object, Function]} Estado con la información de la nueva solución */
  const [newPayload, setNewPayload] = useState({
    title: '',
    description: '',
    createdAt: new Date(),
    createdBy: null,
    deleted: false,
  })

  useEffect(() => {
    if (user?.id) {
      setNewPayload((prev) => ({ ...prev, createdBy: user.id }))
    }
  }, [user])

  /**
   * Maneja los cambios en los campos de texto del formulario.
   * @param {React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>} e - Evento de cambio.
   */
  const handleChange = (e) => {
    const { name, value } = e.target
    setNewPayload((prev) => ({ ...prev, [name]: value }))
  }

  /**
   * Actualiza la descripción (HTML enriquecido) desde el editor.
   * @param {string} html
   */
  const handleDescriptionChange = (html) => {
    setNewPayload((prev) => ({ ...prev, description: html }))
  }

  /**
   * Procesa la subida de archivos adjuntos a Supabase Storage y registra su metadatos.
   * * @async
   * @param {number|string} solutionId - ID de la solución recién creada a la cual se asocian los archivos.
   * @throws {Error} Si ocurre un fallo en la subida al Storage o en la inserción en la base de datos.
   */
  const UploadFiles = async (solutionId) => {
    if (!files || files.length === 0) return

    // Inicializar estado de cada archivo como 'pending'
    setUploadStatus(
      files.map((f) => ({ name: f.file.name, status: 'pending', message: '' }))
    )

    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      const file = f.file
      const fileName = `${ticketId}/${Date.now()}-${file.name}`

      try {
        // 1. Storage
        const { error: storageError } = await supabase.storage
          .from('tickets')
          .upload(fileName, file)

        if (storageError) throw storageError

        // 2. URL pública
        const { data: publicUrlData } = supabase.storage
          .from('tickets')
          .getPublicUrl(fileName)

        // 3. Registro en DB
        const filePayload = {
          ticketId: null,
          solutionId: solutionId,
          commentId: null,
          url: publicUrlData.publicUrl,
          name: file.name,
          size: file.size || 0,
          uploadedBy: user?.id,
          createdAt: new Date(),
          mimeType: file.type,
        }

        const { error: dbError } = await supabase
          .schema('Tickets')
          .from('file')
          .insert([filePayload])
          .select()
          .single()

        if (dbError) throw dbError

        // Actualizar estado como OK
        setUploadStatus((prev) =>
          prev.map((s, idx) =>
            idx === i ? { ...s, status: 'ok', message: 'Subido' } : s
          )
        )
      } catch (err) {
        setUploadStatus((prev) =>
          prev.map((s, idx) =>
            idx === i ? { ...s, status: 'error', message: err.message } : s
          )
        )
        throw new Error(`Error en archivos: ${err.message}`)
      }
    }
  }

  /**
   * Maneja el envío del formulario y coordina las peticiones asíncronas a Supabase.
   * @async
   * @param {React.FormEvent} e - Evento de formulario.
   */
  const handleSubmit = async (e) => {
    e.preventDefault()
    setUploadStatus([])

    if (!newPayload.title.trim() || isHtmlEmpty(newPayload.description)) {
      return sileo.warning({
        title: 'Atención',
        description: 'Por favor, completa el título y la descripción.',
      })
    }

    setLoading(true)

    try {
      // PASO 1: Insertar solución
      const { data: newSolution, error: errorSolution } = await supabase
        .schema('Tickets')
        .from('solution')
        .insert([{ ...newPayload, createdAt: new Date() }])
        .select()
        .single()

      if (errorSolution)
        throw new Error(`Error al crear solución: ${errorSolution.message}`)

      // PASO 2: Relación ticket-solución
      const { error: errorRelation } = await supabase
        .schema('Tickets')
        .from('ticketSolution')
        .insert([{ ticketId: ticketId, solutionId: newSolution.id }])

      if (errorRelation)
        throw new Error(
          `Error al vincular con ticket: ${errorRelation.message}`
        )

      // PASO 3: Archivos
      try {
        await UploadFiles(newSolution.id)
      } catch (fileErr) {
        console.error(
          '[AddSolution] Error en subida de archivos:',
          fileErr.message
        )
        sileo.warning({
          title: 'Archivos no subidos',
          description:
            'La solución se guardó, pero hubo un problema con los adjuntos.',
        })
      }

      sileo.success({
        title: '¡Registrado!',
        description: 'La solución se ha guardado y vinculada correctamente.',
      })

      setNewPayload({
        title: '',
        description: '',
        createdAt: new Date(),
        createdBy: user?.id,
        deleted: false,
      })
      setFiles([])

      if (onAdded) onAdded()
    } catch (err) {
      console.error('❌ Error crítico en AddSolution:', err.message)
      sileo.error({ title: 'Error de registro', description: err.message })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="add-solution-form p-2">
      <div className="mb-3 d-flex flex-column">
        <label className="form-label-slate">Título de la Solución</label>
        <input
          type="text"
          className="form-control-slate"
          name="title"
          placeholder="Ej: Reinicio de servicios de red..."
          value={newPayload.title}
          onChange={handleChange}
          required
        />
      </div>

      <div className="mb-4 d-flex flex-column">
        <label className="form-label-slate">Descripción / Procedimiento</label>
        <RichTextEditor
          value={newPayload.description}
          onChange={handleDescriptionChange}
          ariaLabel="Descripción de la solución"
          placeholder="Describe detalladamente los pasos realizados. Usa la barra para aplicar negrita, cursiva o listas…"
        />
      </div>

      <div className="col-12 mb-3">
        <div className="p-2 border rounded bg-light bg-opacity-10">
          <FileDrop files={files} setFiles={setFiles} />
        </div>
      </div>

      {/* Panel de estado de subida */}
      {uploadStatus.length > 0 && (
        <div className="mb-3">
          <p className="form-label-slate mb-1">Estado de archivos</p>
          <ul className="list-unstyled mb-0">
            {uploadStatus.map((s, i) => (
              <li key={i} className={`upload-status-item status-${s.status}`}>
                <span className="status-icon">
                  {s.status === 'pending' && (
                    <span className="spinner-border spinner-border-sm" />
                  )}
                  {s.status === 'ok' && '✓'}
                  {s.status === 'error' && '✗'}
                </span>
                <span className="status-name">{s.name}</span>
                {s.message && <span className="status-msg">{s.message}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="d-grid">
        <button
          type="submit"
          className="btn btn-primary btn-slate-lg"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Procesando...
            </>
          ) : (
            'Registrar Solución'
          )}
        </button>
      </div>

      <style>{`
        .form-label-slate {
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--text-2);
          margin-bottom: 0.5rem;
        }
        .form-control-slate {
          background: var(--input-bg);
          border: 1px solid var(--input-border);
          border-radius: 10px;
          padding: 0.6rem 1rem;
          font-size: 0.95rem;
          color: var(--text);
          transition: all 0.2s;
        }
        .form-control-slate:focus {
          background: var(--bg-surface);
          border-color: var(--brand);
          box-shadow: 0 0 0 4px var(--gm-primary-soft);
          outline: none;
        }
        .btn-slate-lg {
          padding: 0.8rem;
          border-radius: 10px;
          font-weight: 600;
          box-shadow: 0 4px 6px -1px rgba(var(--gm-primary-rgb), 0.2);
          border: none;
        }

        /* Upload status */
        .upload-status-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.82rem;
          padding: 3px 0;
        }
        .status-icon { width: 16px; text-align: center; }
        .status-name { flex: 1; color: var(--text-2); }
        .status-ok .status-icon   { color: var(--gm-success); }
        .status-error .status-icon { color: var(--gm-danger); }
        .status-msg { font-size: 0.75rem; color: var(--gm-danger); }
      `}</style>
    </form>
  )
}

export default AddSolution
